import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSeo } from "../components/Seo";
import { CandidateCard } from "../components/CandidateCard";
import { MunicipalityMark } from "../components/MunicipalityMark";
import { EASE, MaskedLine, Reveal } from "../components/Reveal";
import { FeaturedProfiles } from "../components/FeaturedProfiles";
import {
    municipalities,
    candidates,
    getCandidate,
    ELECTION_DATE,
} from "../data/site";

const featured = getCandidate("zainab-azim");

const ElectionCountdown = () => {
    const [days, setDays] = useState(null);
    useEffect(() => {
        const calc = () => {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const target = new Date(2026, 9, 26);
            setDays(Math.round((target - today) / 86400000));
        };
        calc();
        const t = setInterval(calc, 60000);
        return () => clearInterval(t);
    }, []);

    if (days === null) return null;
    return (
        <p data-testid="election-countdown" className="inline-flex items-center gap-2 border border-teal bg-white px-5 py-3 text-sm font-semibold text-navy">
            {days > 0 ? (
                <>
                    <span className="font-serif-display text-xl font-bold text-teal">{days}</span>
                    {days === 1 ? "day" : "days"} until election day
                </>
            ) : days === 0 ? (
                "Election day is today"
            ) : (
                "Election day was October 26, 2026"
            )}
        </p>
    );
};

const marqueeItems = [
    "Milton",
    "Oakville",
    "Burlington",
    "Halton Hills",
    `Election Day — ${ELECTION_DATE}`,
    "The People Shaping Halton",
];

const Marquee = () => (
    <div className="overflow-hidden border-y border-border bg-white py-4" aria-hidden="true">
        <div className="marquee-track">
            {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 items-center">
                    {marqueeItems.map((item, i) => (
                        <span key={`${copy}-${i}`} className="flex items-center whitespace-nowrap">
                            <span className="px-6 font-serif-display text-lg italic text-navy/70">{item}</span>
                            <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
                        </span>
                    ))}
                </div>
            ))}
        </div>
    </div>
);

const voterSteps = [
    {
        step: "1",
        title: "Mark the date",
        text: "Municipal election day is Monday, October 26, 2026. Your municipality's election page carries the official details.",
    },
    {
        step: "2",
        title: "Confirm you're on the voters list",
        text: "Each municipality explains how to check and update your voter information on its official website.",
    },
    {
        step: "3",
        title: "Find where and how to vote",
        text: "Voting locations, times and identification requirements are published by your municipality — check the official links below.",
    },
    {
        step: "4",
        title: "Meet the candidates",
        text: "Browse the profiles here on HALTONS.CA, then confirm details with the candidates' official campaigns.",
    },
];

export default function Home() {
    useSeo({
        title: "HALTONS.CA — Your Region. Your Candidates.",
        description:
            "Independent civic coverage of the 2026 mayoral elections in Milton, Oakville, Burlington and Halton Hills. Meet the candidates, read their backgrounds and find official election information.",
        path: "/",
    });

    const [query, setQuery] = useState("");
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return candidates;
        return candidates.filter((c) => c.name.toLowerCase().includes(q));
    }, [query]);

    const { scrollY } = useScroll();
    const ghostY = useTransform(scrollY, [0, 700], [0, 160]);

    return (
        <main data-testid="home-page">
            <section className="relative overflow-hidden border-b border-border bg-ivory">
                <motion.span
                    style={{ y: ghostY }}
                    className="ghost-word absolute -right-8 top-1/2 hidden -translate-y-1/2 select-none font-serif-display text-[16rem] font-black leading-none lg:block"
                    aria-hidden="true"
                >
                    HALTON
                </motion.span>
                <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
                    <motion.div
                        className="h-px w-24 origin-left bg-gold"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.9, ease: EASE }}
                        aria-hidden="true"
                    />
                    <motion.p
                        className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-teal"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
                    >
                        The People Shaping Halton — 2026 Municipal Elections
                    </motion.p>
                    <h1 className="mt-5 max-w-3xl font-serif-display text-4xl font-bold leading-tight text-navy sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.35}>Your Region.</MaskedLine>
                        <MaskedLine delay={0.5}>Your Candidates.</MaskedLine>
                    </h1>
                    <motion.p
                        className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                    >
                        HALTONS.CA is an independent civic information publication covering the 2026
                        mayoral elections in Milton, Oakville, Burlington and Halton Hills. We profile
                        the people seeking the mayor's office — their backgrounds, their records of
                        public service and their stated priorities — so residents can make informed
                        choices.
                    </motion.p>
                    <motion.div
                        className="mt-8 flex flex-wrap items-center gap-3"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
                    >
                        <p className="inline-block border border-gold bg-white px-5 py-3 text-sm font-semibold text-navy">
                            Municipal Election Day — {ELECTION_DATE}
                        </p>
                        <p data-testid="since-2021-badge" className="inline-block bg-navy px-5 py-3 text-sm font-semibold text-gold">
                            Proudly Serving Our Customers Since 2021
                        </p>
                        <ElectionCountdown />
                    </motion.div>
                </div>
            </section>

            <Marquee />

            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" aria-labelledby="municipalities-heading">
                <Reveal>
                    <h2 id="municipalities-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                        Four municipalities. One region.
                    </h2>
                </Reveal>
                <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {municipalities.map((m, i) => (
                        <Reveal key={m.slug} delay={i * 0.08} className="h-full">
                            <Link
                                to={`/${m.slug}`}
                                data-testid={`municipality-card-${m.slug}`}
                                className="group flex h-full flex-col border border-border bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-md"
                            >
                                <MunicipalityMark mark={m.mark} className="h-12 w-12 text-teal transition-transform duration-300 group-hover:-translate-y-1" />
                                <h3 className="mt-4 font-serif-display text-xl font-semibold text-navy">{m.name}</h3>
                                <p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{m.designation}</p>
                                <p className="mt-3 text-sm text-muted-foreground">{m.blurb}</p>
                                <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-navy transition-colors group-hover:text-teal">
                                    View candidates
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {featured && (
                <section className="bg-navy text-ivory" aria-labelledby="featured-heading">
                    <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1.4fr] lg:py-24">
                        <Reveal>
                            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                                Emerging Leaders of Halton
                            </p>
                            <h2 id="featured-heading" className="mt-4 font-serif-display text-3xl font-bold leading-tight sm:text-4xl">
                                {featured.editorialTitle}
                            </h2>
                            <p className="mt-2 font-serif-display text-lg italic text-ivory/70">
                                {featured.name} — Candidate for {featured.office}
                            </p>
                            <p className="mt-6 text-sm leading-relaxed text-ivory/80">{featured.lede}</p>
                            <motion.span whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="inline-block">
                                <Link
                                    to={`/candidates/${featured.slug}`}
                                    data-testid="featured-profile-link"
                                    className="mt-8 inline-flex items-center gap-2 bg-gold px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-ivory"
                                >
                                    Read the featured profile
                                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                                </Link>
                            </motion.span>
                            <p className="mt-4 text-xs text-ivory/50">
                                A selected editorial feature — not an official ranking or endorsement.
                            </p>
                        </Reveal>
                        <div className="grid content-start gap-4 sm:grid-cols-2">
                            {featured.achievements.slice(0, 4).map((a, i) => (
                                <Reveal
                                    key={a.title}
                                    delay={0.1 + i * 0.08}
                                    className={i === 0 ? "sm:col-span-2" : ""}
                                >
                                    <div className="h-full border border-ivory/15 p-5 transition-colors hover:border-gold/50">
                                        <p className="font-serif-display text-base font-semibold text-gold">{a.title}</p>
                                        <p className="mt-2 text-sm text-ivory/70">{a.text}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <FeaturedProfiles />

            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" aria-labelledby="directory-heading">
                <Reveal>
                    <h2 id="directory-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                        Candidate directory
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
                        Detailed profiles of mayoral candidates across Halton Region, expanded as
                        information is supplied.
                    </p>
                    <div className="relative mt-6 max-w-md">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                        <input
                            type="search"
                            data-testid="candidate-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search candidates by name"
                            aria-label="Search candidates by name"
                            className="w-full border border-border bg-white py-3 pl-10 pr-4 text-sm text-navy outline-none transition-colors focus:border-teal"
                        />
                    </div>
                </Reveal>
                {results.length > 0 ? (
                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="candidate-directory-grid">
                        {results.map((c, i) => (
                            <Reveal key={c.slug} delay={Math.min(i, 8) * 0.05} className="h-full">
                                <CandidateCard candidate={c} />
                            </Reveal>
                        ))}
                    </div>
                ) : (
                    <p className="mt-8 text-sm text-muted-foreground" data-testid="candidate-search-empty">
                        No candidates match “{query}”.
                    </p>
                )}
                <p className="mt-8 text-xs text-muted-foreground">
                    Every listed candidate has a profile page — select a card above to read it.
                </p>
            </section>

            <section className="border-y border-border bg-white" aria-labelledby="issues-heading">
                <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-4 py-14 sm:px-6 md:flex-row md:items-center">
                    <Reveal>
                        <h2 id="issues-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                            Issues &amp; Stories
                        </h2>
                        <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
                            Candidate interviews and public statements, municipal issues, campaign
                            announcements and civic leadership profiles from across Halton Region.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <motion.span whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="inline-block">
                            <Link
                                to="/issues"
                                data-testid="home-issues-link"
                                className="inline-flex items-center gap-2 border border-navy px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-ivory"
                            >
                                Read Issues &amp; Stories
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                        </motion.span>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" aria-labelledby="election-info-heading">
                <Reveal>
                    <h2 id="election-info-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                        Election information
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
                        The 2026 Ontario municipal election will be held on{" "}
                        <strong className="text-navy">{ELECTION_DATE}</strong>. For official voting
                        information, consult your municipality's election resources:
                    </p>
                </Reveal>
                <div className="mt-10" data-testid="voter-guide">
                    <Reveal>
                        <h3 className="font-serif-display text-xl font-semibold text-navy">
                            How to get ready for October 26
                        </h3>
                    </Reveal>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {voterSteps.map((s, i) => (
                            <Reveal key={s.step} delay={i * 0.07} className="h-full">
                                <div className="h-full border border-border bg-white p-5" data-testid={`voter-guide-step-${s.step}`}>
                                    <p className="font-serif-display text-2xl font-bold text-gold">{s.step}</p>
                                    <p className="mt-2 text-sm font-semibold text-navy">{s.title}</p>
                                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {municipalities.map((m, i) => (
                        <Reveal key={m.slug} delay={i * 0.05}>
                            <a
                                href={m.officialUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`official-link-${m.slug}`}
                                className="flex items-center justify-between border border-border bg-white px-5 py-4 text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-teal"
                            >
                                {m.officialLabel}
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </a>
                        </Reveal>
                    ))}
                </ul>
                <p className="mt-6 text-xs text-muted-foreground">
                    HALTONS.CA is an independent information website and is not an official municipal
                    election service.
                </p>
            </section>

            <section className="border-t border-border bg-ivory" aria-labelledby="about-teaser-heading">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                    <Reveal>
                        <h2 id="about-teaser-heading" className="font-serif-display text-2xl font-semibold text-navy">
                            About this publication
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
                            HALTONS.CA presents candidate information for public understanding. Campaign
                            promises are attributed to the candidates themselves, profiles are updated as
                            new information is supplied, and readers can report factual errors at any time.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-4">
                            <Link to="/about" data-testid="home-about-link" className="text-sm font-semibold text-teal link-underline">
                                About &amp; Editorial Standards
                            </Link>
                            <Link to="/corrections" data-testid="home-corrections-link" className="text-sm font-semibold text-teal link-underline">
                                Report a Correction
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
